import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MonitorPlay, Images, ListVideo, RefreshCw, Globe, Lock, 
  Shield, Zap, Infinity, Server, Cloud, Clock, Newspaper,
  Store, Coffee, Building2, Hotel, CheckCircle, ArrowRight,
  Play, Sparkles, Heart, Dumbbell, Truck, Scissors,
  TrendingUp, Calendar, DollarSign, Share2
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './LandingPage.css';

const LandingPage = ({ lang = 'de' }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // Set language from URL parameter OR route prop
  useEffect(() => {
    const langParam = searchParams.get('lang');
    
    if (langParam && ['de', 'en', 'fr', 'it'].includes(langParam)) {
      if (i18n.language !== langParam) {
        i18n.changeLanguage(langParam);
      }
    } else if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [searchParams, lang, i18n]);
  
  // Responsive state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const getResponsiveStyles = () => {
    if (windowWidth <= 480) {
      return {
        logoHeight: '140px',
        heroPadding: '4.5rem 1rem 2rem',
        heroMinHeight: 'auto',
        heroTitleSize: '1.6rem',
        heroSubtitleSize: '0.95rem',
      };
    } else if (windowWidth <= 768) {
      return {
        logoHeight: '58px',
        heroPadding: '5rem 1.5rem 3rem',
        heroMinHeight: '65vh',
        heroTitleSize: '2.25rem',
        heroSubtitleSize: '1.1rem',
      };
    } else {
      return {
        logoHeight: '70px',
        heroPadding: '6rem 1.5rem 4rem',
        heroMinHeight: '75vh',
        heroTitleSize: '3.5rem',
        heroSubtitleSize: '1.3rem',
      };
    }
  };
  
  const styles = getResponsiveStyles();
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/');
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            {windowWidth <= 768 ? (
              <img 
                src="/assets/logo/alpenscreen-ultra-transparent-512px.png" 
                alt="AlpenScreen" 
                className="logo-img logo-mobile"
                style={{ height: styles.logoHeight }}
              />
            ) : (
              <img 
                src="/assets/logo/alpenscreen-horizontal-transparent.svg" 
                alt="AlpenScreen" 
                className="logo-img"
                style={{ height: styles.logoHeight }}
              />
            )}
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">{t('landing.nav.features')}</a>
            <a href="#use-cases" className="nav-link">{t('landing.nav.useCases')}</a>
            <a href="#widgets" className="nav-link">{t('landing.nav.widgets')}</a>
            <a href="#pricing" className="nav-link">{t('landing.nav.pricing')}</a>
            
            <div className="nav-right-group">
              <LanguageSwitcher style="landing" />
              
              <button 
                onClick={() => navigate('/login')} 
                className="nav-link-login"
              >
                <span>{t('landing.nav.login')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="hero"
        style={{
          padding: styles.heroPadding,
          minHeight: styles.heroMinHeight
        }}
      >
        {/* Mountain Background */}
        <div className="hero-background">
          <div className="mountain-silhouette"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <svg 
              className="swiss-flag" 
              viewBox="0 0 32 32" 
              width="20" 
              height="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="32" height="32" fill="#DA291C"/>
              <rect x="13" y="6" width="6" height="20" fill="white"/>
              <rect x="6" y="13" width="20" height="6" fill="white"/>
            </svg>
            <span>Swiss Made Digital Signage</span>
          </div>
          
          <h1 
            className="hero-title"
            style={{
              fontSize: styles.heroTitleSize,
            }}
          >
            {t('landing.hero.title').split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < t('landing.hero.title').split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          
          <p 
            className="hero-subtitle"
            style={{ fontSize: styles.heroSubtitleSize }}
          >
            {t('landing.hero.subtitle')}
          </p>
          
          <div className="hero-cta">
            <button 
              onClick={() => navigate('/register')} 
              className="cta-button-primary"
            >
              <Play size={18} strokeWidth={2.5} />
              {t('landing.hero.primaryCTA')}
              <ArrowRight size={18} />
            </button>
            <button 
              onClick={() => scrollToSection('pricing')} 
              className="cta-button-secondary"
            >
              {t('landing.hero.secondaryCTA')}
            </button>
          </div>
          
          <div className="hero-note">
            <div className="hero-note-item">
              <CheckCircle size={18} strokeWidth={2.5} />
              <span>{t('landing.hero.note1')}</span>
            </div>
            <span>·</span>
            <div className="hero-note-item">
              <CheckCircle size={18} strokeWidth={2.5} />
              <span>{t('landing.hero.note2')}</span>
            </div>
            <span>·</span>
            <div className="hero-note-item">
              <CheckCircle size={18} strokeWidth={2.5} />
              <span>{t('landing.hero.note3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <div className="trust-container">
          <div className="trust-item">
            <Shield size={20} strokeWidth={2} color="#2B7FD7" />
            <span>{t('landing.trustBar.swissPrivacy')}</span>
          </div>
          <div className="trust-item">
            <Zap size={20} strokeWidth={2} color="#2B7FD7" />
            <span>{t('landing.trustBar.readyToUse')}</span>
          </div>
          <div className="trust-item">
            <Infinity size={20} strokeWidth={2} color="#2B7FD7" />
            <span>{t('landing.trustBar.flexiblePlans')}</span>
          </div>
          <div className="trust-item">
            <Server size={20} strokeWidth={2} color="#2B7FD7" />
            <span>{t('landing.trustBar.euHosted')}</span>
          </div>
        </div>
      </section>

      {/* Use Cases Section - NEW! */}
      <section id="use-cases" className="use-cases">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              {t('landing.useCases.title')}
            </h2>
            <p className="section-subtitle">
              {t('landing.useCases.subtitle')}
            </p>
          </div>

          <div className="use-cases-grid">
            {/* Restaurant */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Coffee size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.restaurant.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.restaurant.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.restaurant.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.restaurant.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.restaurant.feature3')}</li>
              </ul>
            </div>

            {/* Retail */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Store size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.retail.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.retail.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.retail.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.retail.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.retail.feature3')}</li>
              </ul>
            </div>

            {/* Office */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Building2 size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.office.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.office.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.office.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.office.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.office.feature3')}</li>
              </ul>
            </div>

            {/* Hotel */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Hotel size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.hotel.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.hotel.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.hotel.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.hotel.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.hotel.feature3')}</li>
              </ul>
            </div>

            {/* Gesundheitswesen - NEW */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Heart size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.healthcare.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.healthcare.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.healthcare.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.healthcare.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.healthcare.feature3')}</li>
              </ul>
            </div>

            {/* Fitness & Wellness - NEW */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Dumbbell size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.fitness.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.fitness.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.fitness.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.fitness.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.fitness.feature3')}</li>
              </ul>
            </div>

            {/* Lieferdienste - NEW */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Truck size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.delivery.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.delivery.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.delivery.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.delivery.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.delivery.feature3')}</li>
              </ul>
            </div>

            {/* Beauty & Lifestyle - NEW */}
            <div className="use-case-card">
              <div className="use-case-icon">
                <Scissors size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="use-case-title">{t('landing.useCases.beauty.title')}</h3>
              <p className="use-case-description">
                {t('landing.useCases.beauty.description')}
              </p>
              <ul className="use-case-features">
                <li><CheckCircle size={16} /> {t('landing.useCases.beauty.feature1')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.beauty.feature2')}</li>
                <li><CheckCircle size={16} /> {t('landing.useCases.beauty.feature3')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="features-container">
          <div className="section-header">
            <h2 className="section-title">
              {t('landing.features.sectionTitle')}
            </h2>
            <p className="section-subtitle">
              {t('landing.features.sectionSubtitle')}
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <MonitorPlay size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.screenManagement.title')}</h3>
              <p className="feature-description">
                {t('landing.features.screenManagement.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Images size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.mediaLibrary.title')}</h3>
              <p className="feature-description">
                {t('landing.features.mediaLibrary.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <ListVideo size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.playlistManager.title')}</h3>
              <p className="feature-description">
                {t('landing.features.playlistManager.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <RefreshCw size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.liveUpdates.title')}</h3>
              <p className="feature-description">
                {t('landing.features.liveUpdates.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Globe size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.webBased.title')}</h3>
              <p className="feature-description">
                {t('landing.features.webBased.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Lock size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.features.swissPrivacy.title')}</h3>
              <p className="feature-description">
                {t('landing.features.swissPrivacy.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Widgets Showcase - NEW! */}
      <section id="widgets" className="widgets-showcase">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              {t('landing.widgets.title')}
            </h2>
            <p className="section-subtitle">
              {t('landing.widgets.subtitle')}
            </p>
          </div>

          <div className="modules-grid">
            {/* Weather Module */}
            <div className="module-card">
              <div className="module-icon">
                <Cloud size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.weather.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.weather.description')}
                </p>
              </div>
            </div>

            {/* Clock Module */}
            <div className="module-card">
              <div className="module-icon">
                <Clock size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.clock.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.clock.description')}
                </p>
              </div>
            </div>

            {/* RSS Feed Module */}
            <div className="module-card">
              <div className="module-icon">
                <Newspaper size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.rss.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.rss.description')}
                </p>
              </div>
            </div>

            {/* Web Content Module */}
            <div className="module-card">
              <div className="module-icon">
                <Globe size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.webContent.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.webContent.description')}
                </p>
              </div>
            </div>

            {/* Streaming Module */}
            <div className="module-card">
              <div className="module-icon">
                <Play size={40} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.streaming.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.streaming.description')}
                </p>
              </div>
            </div>

            {/* Analytics Module - COMING SOON */}
            <div className="module-card coming-soon">
              <div className="module-icon">
                <TrendingUp size={40} strokeWidth={1.5} color="#94a3b8" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.analytics.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.analytics.description')}
                </p>
                <span className="coming-soon-badge">{t('landing.widgets.comingSoon')}</span>
              </div>
            </div>

            {/* Calendar Module - COMING SOON */}
            <div className="module-card coming-soon">
              <div className="module-icon">
                <Calendar size={40} strokeWidth={1.5} color="#94a3b8" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.calendar.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.calendar.description')}
                </p>
                <span className="coming-soon-badge">{t('landing.widgets.comingSoon')}</span>
              </div>
            </div>

            {/* Currency Module - COMING SOON */}
            <div className="module-card coming-soon">
              <div className="module-icon">
                <DollarSign size={40} strokeWidth={1.5} color="#94a3b8" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.currency.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.currency.description')}
                </p>
                <span className="coming-soon-badge">{t('landing.widgets.comingSoon')}</span>
              </div>
            </div>

            {/* Social Media Module - COMING SOON */}
            <div className="module-card coming-soon">
              <div className="module-icon">
                <Share2 size={40} strokeWidth={1.5} color="#94a3b8" />
              </div>
              <div className="module-content">
                <h3 className="module-name">{t('landing.widgets.socialMedia.name')}</h3>
                <p className="module-description">
                  {t('landing.widgets.socialMedia.description')}
                </p>
                <span className="coming-soon-badge">{t('landing.widgets.comingSoon')}</span>
              </div>
            </div>
          </div>

          <div className="widgets-coming-soon">
            <p>{t('landing.widgets.moreComing')}</p>
          </div>
        </div>
      </section>

      {/* Pricing Section - 3 TIERS */}
      <section id="pricing" className="pricing">
        <div className="pricing-container">
          <div className="section-header">
            <h2 className="section-title">{t('landing.pricing.title')}</h2>
            <p className="section-subtitle">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="pricing-grid-three">
            {/* STARTER */}
            <div className="pricing-card-modern starter">
              <div className="pricing-header">
                <div className="pricing-badge">{t('landing.pricing.starter.badge')}</div>
                <h3 className="pricing-name">{t('landing.pricing.starter.name')}</h3>
                <div className="pricing-price-large">
                  <span className="currency">CHF</span>
                  <span className="amount">29</span>
                  <span className="period">/Monat</span>
                </div>
                <p className="pricing-description">{t('landing.pricing.starter.description')}</p>
              </div>
              
              <ul className="pricing-features-modern">
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.starter.feature1')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.starter.feature2')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.starter.feature3')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.starter.feature4')}</span>
                </li>
              </ul>
              
              <button 
                onClick={() => navigate('/register')} 
                className="pricing-cta-modern starter"
              >
                {t('landing.pricing.starter.cta')}
                <ArrowRight size={18} />
              </button>
              
              <p className="pricing-trial">{t('landing.pricing.trial')}</p>
            </div>

            {/* BUSINESS - Popular */}
            <div className="pricing-card-modern business popular">
              <div className="popular-ribbon">{t('landing.pricing.business.popular')}</div>
              <div className="pricing-header">
                <div className="pricing-badge">{t('landing.pricing.business.badge')}</div>
                <h3 className="pricing-name">{t('landing.pricing.business.name')}</h3>
                <div className="pricing-price-large">
                  <span className="currency">CHF</span>
                  <span className="amount">99</span>
                  <span className="period">/Monat</span>
                </div>
                <p className="pricing-description">{t('landing.pricing.business.description')}</p>
              </div>
              
              <ul className="pricing-features-modern">
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.business.feature1')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.business.feature2')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.business.feature3')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.business.feature4')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.business.feature5')}</span>
                </li>
              </ul>
              
              <button 
                onClick={() => navigate('/register')} 
                className="pricing-cta-modern business"
              >
                {t('landing.pricing.business.cta')}
                <ArrowRight size={18} />
              </button>
              
              <p className="pricing-trial">{t('landing.pricing.trial')}</p>
            </div>

            {/* ENTERPRISE */}
            <div className="pricing-card-modern enterprise">
              <div className="pricing-header">
                <div className="pricing-badge">{t('landing.pricing.enterprise.badge')}</div>
                <h3 className="pricing-name">{t('landing.pricing.enterprise.name')}</h3>
                <div className="pricing-price-large">
                  <span className="currency">CHF</span>
                  <span className="amount">299</span>
                  <span className="period">/Monat</span>
                </div>
                <p className="pricing-description">{t('landing.pricing.enterprise.description')}</p>
              </div>
              
              <ul className="pricing-features-modern">
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.enterprise.feature1')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.enterprise.feature2')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.enterprise.feature3')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.enterprise.feature4')}</span>
                </li>
                <li>
                  <CheckCircle size={20} strokeWidth={2.5} color="#10b981" />
                  <span>{t('landing.pricing.enterprise.feature5')}</span>
                </li>
              </ul>
              
              <button 
                className="pricing-cta-modern enterprise"
                disabled
              >
                {t('landing.pricing.enterprise.cta')}
              </button>
              
              <p className="pricing-trial">{t('landing.pricing.enterprise.comingSoon')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Swiss Advantage Section */}
      <section className="swiss-advantage">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('landing.swissAdvantage.title')}</h2>
            <p className="section-subtitle">{t('landing.swissAdvantage.subtitle')}</p>
          </div>

          <div className="swiss-features-grid">
            {/* Privacy */}
            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.swissAdvantage.privacy.title')}</h3>
              <p className="feature-description">
                {t('landing.swissAdvantage.privacy.description')}
              </p>
            </div>

            {/* Multilingual */}
            <div className="feature-card">
              <div className="feature-icon">
                <Globe size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.swissAdvantage.multilingual.title')}</h3>
              <p className="feature-description">
                {t('landing.swissAdvantage.multilingual.description')}
              </p>
            </div>

            {/* Support */}
            <div className="feature-card">
              <div className="feature-icon">
                <Zap size={48} strokeWidth={1.5} color="#2B7FD7" />
              </div>
              <h3 className="feature-title">{t('landing.swissAdvantage.support.title')}</h3>
              <p className="feature-description">
                {t('landing.swissAdvantage.support.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="faq-container">
          <div className="section-header">
            <h2 className="section-title">{t('landing.faq.title')}</h2>
            <p className="section-subtitle">
              {t('landing.faq.subtitle')}
            </p>
          </div>
          <div className="faq-grid">
            {[1, 2, 3, 4, 5, 6].map(num => (
              <div key={num} className="faq-item">
                <h3 className="faq-question">{t(`landing.faq.q${num}.question`)}</h3>
                <p className="faq-answer">{t(`landing.faq.q${num}.answer`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="final-cta-content">
          <h2 className="final-cta-title">{t('landing.finalCTA.title')}</h2>
          <p className="final-cta-subtitle">
            {t('landing.finalCTA.subtitle')}
          </p>
          <button 
            onClick={() => navigate('/register')} 
            className="cta-button-primary large"
          >
            <Play size={20} strokeWidth={2.5} />
            {t('landing.finalCTA.button')}
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content-simple">
            <div className="footer-brand-center">
              <img 
                src="/assets/logo/alpenscreen-icon-transparent.svg" 
                alt="AlpenScreen" 
                className="footer-logo-icon" 
              />
              <p className="footer-brand-text">ALPENSCREEN</p>
            </div>
            <div className="footer-links-simple">
              <a href="#features" className="footer-link">{t('landing.footer.features')}</a>
              <span className="footer-separator">•</span>
              <a href="#use-cases" className="footer-link">{t('landing.footer.useCases')}</a>
              <span className="footer-separator">•</span>
              <a href="#widgets" className="footer-link">{t('landing.footer.widgets')}</a>
              <span className="footer-separator">•</span>
              <a href="#pricing" className="footer-link">{t('landing.footer.pricing')}</a>
              <span className="footer-separator">•</span>
              <button onClick={() => navigate('/support')} className="footer-link-button">
                {t('landing.footer.support')}
              </button>
              <span className="footer-separator">•</span>
              <button onClick={() => navigate('/privacy')} className="footer-link-button">
                {t('landing.footer.privacy')}
              </button>
              <span className="footer-separator">•</span>
              <button onClick={() => navigate('/terms')} className="footer-link-button">
                {t('landing.footer.terms')}
              </button>
              <span className="footer-separator">•</span>
              <button onClick={() => navigate('/imprint')} className="footer-link-button">
                {t('landing.footer.imprint')}
              </button>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;